import React, { useRef, useEffect, useState } from 'react';
import { Button, MapAreaData } from '../styling/StyledComponents';
import mapboxgl from 'mapbox-gl';
import Cookies from 'js-cookie';
import { addBuildings } from '../MapLayers/BuildingLayer';
import { LocationData } from '../Geolocation/LocationProvider';
import axios from 'axios';
import { createMarkers } from '../MapLayers/UserDataLayer';
import 'mapbox-gl/dist/mapbox-gl.css';
import StaticColorIndicator from '../Components/Color/StaticColorIndicator';
import { CSVLink } from "react-csv";
import ChatAssistant from '../Components/ChatAssistant';


function UsersDataMap({ selectedProjectId, selectedQuestionId }) {
    mapboxgl.accessToken = Cookies.get('mapbox_token');
    const [location, setLocation] = LocationData();
    const [zoom, setZoom] = useState(12.3);
    const [filteredData, setFilteredData] = useState([]);
    const [diffRating, setDiffRating] = useState(0);
    const [minRating, setMinRating] = useState(0);

    const mapContainer = useRef(null);
    const map = useRef(null);
    const markersRef = useRef([]);

    // Function to clear all markers and layers
    const clearAllMarkers = () => {
        // Remove all markers
        markersRef.current.forEach(marker => marker.remove());
        markersRef.current = [];
        
        // Remove layers and sources if they exist
        if (map.current) {
            if (map.current.getLayer('user-data-points')) {
                map.current.removeLayer('user-data-points');
            }
            if (map.current.getSource('user-data-points')) {
                map.current.removeSource('user-data-points');
            }
        }
    };

    // Fetch and filter data
    useEffect(() => {
        const fetchData = async () => {
            try {
                // Clear existing data and markers
                setFilteredData([]);
                clearAllMarkers();
                
                if (selectedProjectId) {
                    const response = await axios.post(
                        `${process.env.REACT_APP_BACKEND_URL}/answers/filtered`,
                        {
                            projectId: selectedProjectId,
                            questionId: selectedQuestionId
                        },
                        {
                            headers: {
                                Authorization: `Bearer ${Cookies.get('token')}`
                            },
                            withCredentials: true
                        }
                    );

                    if (response.data.success) {
                        setFilteredData(response.data.result);
                    }
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                setFilteredData([]);
                clearAllMarkers();
            }
        };

        fetchData();
    }, [selectedProjectId, selectedQuestionId]);

    // Initialize map
    useEffect(() => {
        if (!mapboxgl.accessToken || map.current) return;

        try {
            map.current = new mapboxgl.Map({
                container: mapContainer.current,
                style: process.env.REACT_APP_CUSTOM_MAP,
                center: [location.lng, location.lat],
                zoom: zoom,
                pitch: 30,
                dragPan: true,
                doubleClickZoom: true,
                scrollZoom: true,
                dragRotate: true,
            });

            map.current.on('move', () => {
                setLocation({
                    lng: map.current.getCenter().lng.toFixed(4),
                    lat: map.current.getCenter().lat.toFixed(4)
                });
                setZoom(map.current.getZoom().toFixed(2));
            });

            map.current.on('load', () => {
                try {
                    addBuildings(map.current);
                } catch (err) {
                    console.error("Error adding buildings:", err);
                }
            });
        } catch (err) {
            console.error("Map initialization error:", err);
        }

        return () => {
            clearAllMarkers();
            if (map.current) {
                map.current.remove();
                map.current = null;
            }
        };
    }, []);

    // Update markers when filteredData changes
    useEffect(() => {
        if (!map.current) return;

        try {
            clearAllMarkers();
            
            if (filteredData.length > 0) {
                const [max_diff_rating, min_rating, markers] = createMarkers(filteredData, map.current);
                setDiffRating(max_diff_rating);
                setMinRating(min_rating);
                markersRef.current = markers;
            } else {
                setDiffRating(1);
                setMinRating(1);
            }
        } catch (err) {
            console.error("Error updating markers:", err);
        }
    }, [filteredData]);

    return (
        <div className='fill-height'>
            <div className='flex-row right'>
                <StaticColorIndicator diff_rating={diffRating} min_rating={minRating} />
            </div>
            <div className='margin-top overlay'>
                <CSVLink 
                    className='text-color padding-m' 
                    data={filteredData} 
                    separator={";"} 
                    filename={"questionnaire_data.csv"}
                >
                    <Button>Download</Button>
                </CSVLink>
            </div>
            <MapAreaData className='fill-height' ref={mapContainer} />
        </div>
    );
}

export default UsersDataMap;